/** @jsxRuntime classic */
/** @jsx jsx */
import { css, Global, jsx } from "@emotion/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@material-ui/core";
import { PCView, SPView } from "components/common/MediaQuery";
import { SETTLEMENT_FEE_DATA } from "constants/purchase";
import { useLiveCommerceShippingFee } from "hooks/Settlement";
import { useI18n } from "hooks/i18n/i18n";
import { useLegacyPortalAddressInfo } from "hooks/liveCommerce/useLegacyPortalAddressInfo";
import { cartActions } from "modules/cart";
import { purchaseActions } from "modules/purchase";
import { streamingActions } from "modules/streaming";
import React, { useEffect } from "react";
import { useForm, FormProvider } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useSelector } from "store";
import { AddedToCartToast } from "ui/Streaming/LiveCommerce/AddedToCartToast";
import { z } from "zod/v3";

import { LiveCommerceFooter } from "./liveCommerce/LiveCommerceFooter";
import StreamingLiveCommerceCartConfirmV2 from "./liveCommerce/StreamingLiveCommerceCartConfirmV2";
import StreamingLiveCommerceCartV2 from "./liveCommerce/StreamingLiveCommerceCartV2";
import StreamingLiveCommerceDisplayInfo from "./liveCommerce/StreamingLiveCommerceDisplayInfo";
import StreamingLiveCommerceGoodsListV2 from "./liveCommerce/StreamingLiveCommerceGoodsListV2";
import { ErrorModal } from "./liveCommerce/StreamingLiveCommerceModal";
import { CompletePurchaseV2 } from "./liveCommerce/StreamingLiveCommerceModalV2";
import { useLiveCommerce } from "./useLiveCommerce";

import type { ProductData } from "@spwn/types/firebase/firestore";
import type { TFunction } from "i18next";
import type { ReqAddToCartAndPurchase } from "modules/cart";

const createCardValidationSchema = (t: TFunction) =>
  z.object({
    paymentType: z.literal("Card"),
    selectedCreditCardSeq: z
      .string()
      .min(
        1,
        t("streaming.liveCommerce.cardValidation.selectedCreditCardSeq.min")
      )
      .nullable(),
    cardSecurityCode: z
      .string()
      .regex(
        /^\d{3,4}$/,
        t("streaming.liveCommerce.cardValidation.cardSecurityCode.regex")
      ),
  });

const createCvsValidationSchema = (t: TFunction) =>
  z.object({
    paymentType: z.literal("CVS"),
    selectedCvsCode: z.string(),
    customerName: z
      .string()
      .min(1, t("streaming.liveCommerce.cvsValidation.customerName.min")),
    customerKana: z
      .string()
      .min(1, t("streaming.liveCommerce.cvsValidation.customerKana.min")),
    telNo: z
      .string()
      .min(1, t("streaming.liveCommerce.cvsValidation.telNo.min"))
      .regex(
        /^\d{10,11}$/,
        t("streaming.liveCommerce.cvsValidation.telNo.regex")
      ),
  });

const phoneValidationSchema = z.object({
  paymentType: z.literal("Phone"),
  phoneCode: z.string(),
});

const createPaymentValidationSchema = (t: TFunction) =>
  z.discriminatedUnion("paymentType", [
    createCardValidationSchema(t),
    createCvsValidationSchema(t),
    phoneValidationSchema,
  ]);

export type CreditCardPaymentFields = z.infer<
  ReturnType<typeof createCardValidationSchema>
>;
export type PaymentFields = z.infer<
  ReturnType<typeof createPaymentValidationSchema>
>;

const StreamingLiveCommerceV2: React.FC<{
  mainVideoId: string;
  THEOPlayerElementHeight: number;
  setSelectedPaymentType: (type: "Card" | "CVS" | "Phone") => void;
  setShowPurchasingModal: (show: boolean) => void;
}> = ({
  mainVideoId,
  THEOPlayerElementHeight,
  setSelectedPaymentType,
  setShowPurchasingModal,
}) => {
  const {
    isPurchased,
    pseudoCart,
    selectedLiveCommerceGoods,
    cartTotalNum,
    currentView,
    errorInfo,
    setCurrentView,
    setSelectType,
    incrementCartItem,
    decrementCartItem,
    addToCart,
    addToCartToBuy,
    removeCartItem,
  } = useLiveCommerce();

  const [showToast, setShowToast] = React.useState<boolean>(false);
  const [goodsInfo, setGoodsInfo] = React.useState<ProductData | null>(null);

  const eventGoodsMap = useSelector((state) => state.ticket.eventGoodsMap);
  const displayEvent = useSelector((state) => state.event.displayEvent);
  const isAddressRegistered = useSelector(
    (state) => state.auth.isAddressRegistered
  );
  const registeredCardList = useSelector(
    (state) => state.purchase.cardInfoList
  );
  const { addressInfo, error } =
    useLegacyPortalAddressInfo(isAddressRegistered);

  if (error) {
    console.error("住所の取得に失敗しました:", error);
  }

  const { t } = useI18n();
  const dispatch = useDispatch();

  const { isCalculatingShippingFee, shippingFee, isError } =
    useLiveCommerceShippingFee(displayEvent, pseudoCart);

  const paymentValidationSchema = React.useMemo(
    () => createPaymentValidationSchema(t),
    [t]
  );

  const methods = useForm<PaymentFields>({
    resolver: zodResolver(paymentValidationSchema),
    mode: "onChange",
  });

  const {
    watch,
    setValue,
    formState: { isValid },
  } = methods;
  const selectedPaymentType = watch("paymentType");
  const selectedCreditCardSeq = watch("selectedCreditCardSeq");
  const selectedSecurityCode = watch("cardSecurityCode");
  const selectedCvsCode = watch("selectedCvsCode");
  const customerName = watch("customerName");
  const customerKana = watch("customerKana");
  const telNo = watch("telNo");
  const phoneCode = watch("phoneCode");

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 1500);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [showToast]);

  useEffect(() => {
    if (
      registeredCardList &&
      registeredCardList.length !== 0 &&
      !selectedCreditCardSeq
    ) {
      setValue("selectedCreditCardSeq", registeredCardList[0]?.cardSeq ?? null);
    }
  }, [selectedCreditCardSeq, registeredCardList, setValue]);
  useEffect(() => {
    dispatch(purchaseActions.refreshFingerprint());
    return () => {
      clearLiveCommerceCart();
      closeErrorModal();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (isPurchased || errorInfo.isError) {
      setShowPurchasingModal(false);
    }
  }, [isPurchased, errorInfo.isError, setShowPurchasingModal]);

  const settlementFee = selectedPaymentType
    ? SETTLEMENT_FEE_DATA[selectedPaymentType]
    : 0;

  // TODO 修正必要(?)
  const clearLiveCommerceCart = () => {
    dispatch(purchaseActions.refreshFingerprint());
    dispatch(streamingActions.successPurchaseLiveCommerce(false));
    setCurrentView("goodsList");
  };

  const closeErrorModal = () => {
    dispatch(
      streamingActions.toggleLiveCommerceError({
        isError: false,
        caption: "",
        msg: "",
      })
    );
  };

  const handleAddToCart = (classGoodsList: ProductData[]) => {
    addToCart(classGoodsList);
    setShowToast(true);
  };

  const openGoodsInfo = (goodsItem: ProductData) => {
    setCurrentView("goodsInfo");
    setGoodsInfo(goodsItem);
  };

  const closeGoodsInfo = () => {
    setCurrentView("goodsList");
    setGoodsInfo(null);
  };
  const isDisabledPurchaseButton = (() => {
    if (cartTotalNum === 0) {
      return true;
    }
    if (currentView === "cart" || currentView === "confirm") {
      return !isValid;
    }
    return false;
  })();

  const handlePurchase = () => {
    if (pseudoCart.length === 0) {
      console.error("カートが空です");
      return;
    }

    setShowPurchasingModal(true);

    const purchaseItem = pseudoCart
      .map((item) => ({
        id: item._id,
        productType: item.productType,
        count: item.count,
      }))
      .filter((item) => item.count > 0);

    switch (selectedPaymentType) {
      case "Card": {
        if (!selectedCreditCardSeq || !selectedSecurityCode) {
          console.error("クレジットカードが選択されていません");
          return;
        }
        setSelectedPaymentType("Card");
        const cardSeq = Number(selectedCreditCardSeq);
        dispatch(
          cartActions.addToCartAndPurchase.started({
            eventId: displayEvent.eventId,
            currentVideoId: mainVideoId,
            products: purchaseItem,
            displayPrice: totalFee,
            cardSeq,
            securityCode: selectedSecurityCode,
            purchaseMethod: "Card",
            source: "liveCommerce",
          } as ReqAddToCartAndPurchase<"Card">)
        );
        break;
      }

      case "CVS":
        if (!selectedCvsCode || !customerName || !customerKana || !telNo) {
          console.error("コンビニ決済に必要な情報が不足しています");
          return;
        }
        setSelectedPaymentType("CVS");
        dispatch(
          cartActions.addToCartAndPurchase.started({
            eventId: displayEvent.eventId,
            currentVideoId: mainVideoId,
            products: purchaseItem,
            displayPrice: totalFee,
            cvesCode: selectedCvsCode,
            customerName,
            customerKana,
            telNo,
            purchaseMethod: "CVS",
            source: "liveCommerce",
          } as ReqAddToCartAndPurchase<"CVS">)
        );
        break;
      case "Phone":
        //TODO: キャリア決済処理を実装
        if (!phoneCode) {
          console.error("決済方法が選択されていません");
          return;
        }
        setSelectedPaymentType("Phone");
        dispatch(
          cartActions.addToCartAndPurchase.started({
            eventId: displayEvent.eventId,
            currentVideoId: mainVideoId,
            products: purchaseItem,
            displayPrice: totalFee,
            phoneCode,
            purchaseMethod: "Phone",
            source: "liveCommerce",
          } as ReqAddToCartAndPurchase<"Phone">)
        );
        break;
      default:
        console.error("支払い方法が不正です");
        return;
    }
  };

  const footerButtonLabel =
    currentView === "confirm"
      ? t("streaming.commerceCart.purchase")
      : t("streaming.commerceGoodsList.next");

  const handleOnClickFooterButton = () => {
    if (currentView === "goodsList") {
      setCurrentView("cart");
    } else if (currentView === "cart") {
      setCurrentView("confirm");
    } else if (currentView === "confirm") {
      handlePurchase();
    }
  };

  const cartTotalFee = pseudoCart.reduce(
    (sum, item) => sum + item.price_jpy * item.count,
    0
  );

  const totalFee = cartTotalFee + shippingFee + settlementFee;

  if (!eventGoodsMap) {
    return null;
  }

  if (errorInfo.isError) {
    return (
      <ErrorModal
        caption={errorInfo.caption}
        msg={errorInfo.msg}
        closeModal={closeErrorModal}
      />
    );
  }

  if (isPurchased && selectedPaymentType === "Phone") {
    //TODO: 別ウィンドウに遷移する表示を新デザインに変更
    return <CompletePurchaseV2 clearLiveCommerceCart={clearLiveCommerceCart} />;
  }

  if (currentView === "goodsInfo" && goodsInfo) {
    return (
      <StreamingLiveCommerceDisplayInfo
        goodsInfo={goodsInfo}
        THEOPlayerElementHeight={THEOPlayerElementHeight}
        closeGoodsInfo={closeGoodsInfo}
      />
    );
  }

  return (
    <FormProvider {...methods}>
      <React.Fragment>
        <Global styles={liveCommerceModalOverrideStyles} />

        {currentView === "goodsList" && (
          <React.Fragment>
            <StreamingLiveCommerceGoodsListV2
              THEOPlayerElementHeight={THEOPlayerElementHeight}
              pseudoCart={pseudoCart}
              selectedLiveCommerceGoods={selectedLiveCommerceGoods}
              setType={setSelectType}
              addToCart={handleAddToCart}
              addToCartToBuy={addToCartToBuy}
              openGoodsInfo={openGoodsInfo}
              openLiveCommerceCart={() => setCurrentView("cart")}
              incrementCartItem={incrementCartItem}
              decrementCartItem={decrementCartItem}
            />

            <AddedToCartToast showToast={showToast} />
          </React.Fragment>
        )}

        {currentView === "cart" && (
          <StreamingLiveCommerceCartV2
            THEOPlayerElementHeight={THEOPlayerElementHeight}
            pseudoCart={pseudoCart}
            shippingFee={shippingFee}
            cartTotalFee={cartTotalFee}
            totalFee={totalFee}
            settlementFee={settlementFee}
            isCalculatingShippingFee={isCalculatingShippingFee}
            isError={isError}
            addressInfo={addressInfo}
            incrementCartItem={incrementCartItem}
            decrementCartItem={decrementCartItem}
            removeCartItem={removeCartItem}
            closeLiveCommerceCart={() => setCurrentView("goodsList")}
            proceedToCheckout={() => {
              setCurrentView("confirm");
            }}
          />
        )}

        {currentView === "confirm" && (
          <StreamingLiveCommerceCartConfirmV2
            playerHeight={THEOPlayerElementHeight}
            addressInfo={addressInfo}
            paymentMethod={selectedPaymentType}
            pseudoCart={pseudoCart}
            displayEvent={displayEvent}
            cartTotalFee={cartTotalFee}
            shippingFee={shippingFee}
            settlementFee={settlementFee}
            totalFee={totalFee}
            handleBackToCart={() => setCurrentView("cart")}
            handlePurchase={handlePurchase}
            decrementCartItem={decrementCartItem}
            incrementCartItem={incrementCartItem}
          />
        )}

        <PCView>
          <Box position={"absolute"} bottom={0} width={"100%"} zIndex={"10"}>
            <LiveCommerceFooter
              isDisabled={isDisabledPurchaseButton}
              itemNum={cartTotalNum}
              pseudoCart={pseudoCart}
              buttonLabel={footerButtonLabel}
              onClickButton={handleOnClickFooterButton}
              decrementGoodsCount={decrementCartItem}
              incrementGoodsCount={incrementCartItem}
            />
          </Box>
        </PCView>

        <SPView>
          <Box
            position={"fixed"}
            bottom={0}
            left={0}
            width={"100%"}
            zIndex={"10"}
          >
            <LiveCommerceFooter
              isDisabled={isDisabledPurchaseButton}
              itemNum={cartTotalNum}
              pseudoCart={pseudoCart}
              buttonLabel={footerButtonLabel}
              onClickButton={handleOnClickFooterButton}
              decrementGoodsCount={decrementCartItem}
              incrementGoodsCount={incrementCartItem}
            />
          </Box>
        </SPView>
      </React.Fragment>
    </FormProvider>
  );
};

// TODO: global 経由でのモーダルスタイル注入をしている。適切な方法を考えたい
const liveCommerceModalOverrideStyles = css`
  .loading_layout {
    display: flex;
    align-items: center;
    height: 100% !important;
    margin: 0px auto 0 !important;
    padding: 0px !important;
    background-color: initial !important;
    &:focus {
      outline: none;
    }
    .sk-cube-grid {
      margin: 0 auto !important;
    }
    .sk-cube {
      background-color: #ffffff !important;
    }
    h2 {
      display: none !important;
    }
    .popup_message {
      display: none !important;
    }
  }
`;

export default StreamingLiveCommerceV2;
